import axios from 'axios';
import { SendToCCPayload } from '../components/LMA/types';
import { post } from 'lib/utils/axiosFetcher';
import { withRetry } from 'lib/utils/withRetry';
import { withLmaSafeApiLogging } from '../lib/utils/lmaSafeLogging';

export interface ReferralDataResponse {
  data: any[];
  totalRecords: number;
  success: boolean;
  message?: string;
}

export interface FilterParams {
  [key: string]: string;
}

export interface PaginationParams {
  page: number;
  pageSize: number;
}

export interface FetchReferralDataPayload {
  leadStatusParams?: Record<string, string>;
  filters?: FilterParams | null;
  pagination?: PaginationParams;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  configuration?: {
    leads: Record<string, string>;
  };
}

const objectToQueryString = (obj: any) => {
  const pairs: string[] = [];
  for (const key of Object.keys(obj)) {
    const value = obj[key];
    if (value === undefined || value === null) continue;
    const encKey = encodeURIComponent(key);
    if (Array.isArray(value)) {
      for (const item of value) {
        if (item === undefined || item === null) continue;
        pairs.push(`${encKey}=${encodeURIComponent(String(item))}`);
      }
    } else {
      pairs.push(`${encKey}=${encodeURIComponent(String(value))}`);
    }
  }
  return pairs.join('&');
};

/** Comma-separated or single values → parallel arrays for GetSyncedResources (must stay aligned). */
export function normalizeStringListForApi(input: string | string[] | undefined | null): string[] {
  if (input == null) return [];
  return (Array.isArray(input) ? input : String(input).split(','))
    .flatMap((v) => String(v).split(','))
    .map((s) => s.trim())
    .filter(Boolean);
}

export function normalizeLeadIdsForApi(
  input: number | string | (number | string)[] | undefined | null
): number[] {
  if (input == null) return [];
  if (Array.isArray(input)) {
    return input
      .flatMap((v) => (typeof v === 'string' ? v.split(',') : [String(v)]))
      .map((s) => s.trim())
      .filter(Boolean)
      .map((s) => Number(s))
      .filter((n) => !Number.isNaN(n));
  }
  if (typeof input === 'number') return Number.isFinite(input) ? [Math.trunc(input)] : [];
  return String(input)
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean)
    .map((s) => Number(s))
    .filter((n) => !Number.isNaN(n));
}

/**
 * Fetch referral data with POST method supporting filters and pagination
 * This API now handles server-side filtering, pagination, and sorting
 * leadType and leadStatusParams are sent as URL query params
 * Filters, Page, and PageSize are sent in POST body
 */
export const fetchReferralData = async (
  params: any,
  payload: FetchReferralDataPayload
): Promise<ReferralDataResponse> => {
  const url = `/LMA/GetReferralData?${objectToQueryString(params)}`;

  // Clean filters - remove empty string values
  const cleanedFilters = Object.entries(payload.filters || {})
    .filter(([, value]) => value !== '' && value != null)
    .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {});

  payload.filters = Object.keys(cleanedFilters).length === 0 ? null : cleanedFilters;

  // POST body contains Filters, Page, PageSize, and optional sorting parameters
  const response = await withLmaSafeApiLogging(
    {
      operation: 'fetchReferralData',
      method: 'POST',
      endpoint: url,
    },
    () => withRetry(() => post(url, payload))
  );

  return response.data;
};

export const fetchNonReferralData = async (
  params: any,
  payload: FetchReferralDataPayload
): Promise<ReferralDataResponse> => {
  const url = `/LMA/GetNonReferralData?${objectToQueryString(params)}`;

  // Clean filters - remove empty string values
  const cleanedFilters = Object.entries(payload.filters || {})
    .filter(([, value]) => value !== '' && value != null)
    .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {});

  payload.filters = Object.keys(cleanedFilters).length === 0 ? null : cleanedFilters;

  // POST body contains Filters, Page, PageSize, and optional sorting parameters
  const response = await withLmaSafeApiLogging(
    {
      operation: 'fetchNonReferralData',
      method: 'POST',
      endpoint: url,
    },
    () => withRetry(() => post(url, payload))
  );

  return response.data;
};

export const fetchProcessedReferralData = async (
  params: any,
  payload: FetchReferralDataPayload,
  /** Profile view → GetFullProfileProcessedData; enrollment → GetProcessedData */
  useFullProfileProcessed = true
): Promise<any> => {
  const processedPath = useFullProfileProcessed
    ? '/LMA/GetFullProfileProcessedData'
    : '/LMA/GetProcessedData';
  const url = `${processedPath}?${objectToQueryString(params)}`;

  // Clean filters - remove empty string values
  const cleanedFilters = Object.entries(payload.filters || {})
    .filter(([, value]) => value !== '' && value != null)
    .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {});

  payload.filters = Object.keys(cleanedFilters).length === 0 ? null : cleanedFilters;

  const response = await withLmaSafeApiLogging(
    {
      operation: 'fetchProcessedReferralData',
      method: 'POST',
      endpoint: url,
    },
    () => withRetry(() => post(url, payload))
  );

  return response.data;
};

export const fetchFlaggedRecordsData = async (
  params: any,
  payload: FetchReferralDataPayload
): Promise<ReferralDataResponse> => {
  const url = `/LMA/GetFlaggedRecordsData?${objectToQueryString(params)}`;

  // Clean filters - remove empty string values
  const cleanedFilters = Object.entries(payload.filters || {})
    .filter(([, value]) => value !== '' && value != null)
    .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {});

  payload.filters = Object.keys(cleanedFilters).length === 0 ? null : cleanedFilters;

  // POST body contains Filters, Page, PageSize, and optional sorting parameters
  const response = await withLmaSafeApiLogging(
    {
      operation: 'fetchFlaggedRecordsData',
      method: 'POST',
      endpoint: url,
    },
    () => withRetry(() => post(url, payload))
  );

  return response.data;
};

//Calls the backend API to add an LMA user.
export const addLMAUser = async (userName: string): Promise<any> => {
  const response = await withLmaSafeApiLogging(
    {
      operation: 'addLMAUser',
      method: 'POST',
      endpoint: '/LMA/AddLMAUser',
    },
    () =>
      axios.post('/api/dap-api', {
        url: '/LMA/AddLMAUser',
        data: { userName }, // wrap in object for backend compatibility
      })
  );
  return response?.data;
};

// ============================================
// Flag Patient API
// ============================================

/**
 * Payload for the UpdateLeadFlaggedStatus endpoint
 * Matches backend FlaggedLeadStatusRequest model
 */
export interface FlagPatientPayload {
  leadId: number;
  userId: number;
  leadStatus: string; // "Flagged" to flag, anything else to unflag (backend resets to "Requested")
  ccPatientId: string;
}

/**
 * Flags or unflags a patient record
 *
 * @param payload - The flag request data matching FlaggedLeadStatusRequest
 * @returns Promise<boolean> - true if the operation succeeded
 * @throws Error if the API call fails
 */
export const flagPatient = async (payload: FlagPatientPayload): Promise<boolean> => {
  const response = await withLmaSafeApiLogging(
    {
      operation: 'flagPatient',
      method: 'POST',
      endpoint: '/LMA/UpdateLeadFlaggedStatus',
    },
    () =>
      axios.post('/api/dap-api', {
        url: '/LMA/UpdateLeadFlaggedStatus',
        data: payload,
      })
  );
  return response?.data === true;
};

// ============================================
// Lead Lock Storage — Redis / SQL routing
// ============================================

/**
 * Module-level lock storage type. Set once from LeadManagement.tsx (Sitecore field).
 * Also persisted to localStorage so Logout.tsx can read it on a fresh page load.
 */
let _lockStorageType: 'Redis' | 'SQL' = 'Redis';

/** Call once from LeadManagement.tsx to set the lock storage backend. */
export const setLockStorageType = (type: 'Redis' | 'SQL') => {
  _lockStorageType = type;
  if (typeof window !== 'undefined') {
    localStorage.setItem('lma-lock-storage-type', type);
  }
};

/** Read current storage type. Falls back to localStorage for pages outside LeadManagement (e.g. Logout, Admin). */
export const getLockStorageType = (): 'Redis' | 'SQL' => {
  if (_lockStorageType !== 'Redis') return _lockStorageType;
  if (typeof window !== 'undefined') {
    const stored = localStorage.getItem('lma-lock-storage-type');
    if (stored === 'SQL') {
      _lockStorageType = 'SQL';
      return 'SQL';
    }
  }
  return 'Redis';
};

/**
 * Module-level lock timeout (seconds). Set once from LeadManagement.tsx (Sitecore LockTimeoutSeconds field).
 * Passed to the API on tryLeadLock so Redis uses it as the key TTL.
 * Falls back to null (API route uses its own env-based default).
 */
let _lockTimeoutSeconds: number | null = null;

/** Call once from LeadManagement.tsx to set the lock timeout from Sitecore. */
export const setLockTimeoutSeconds = (seconds: number) => {
  _lockTimeoutSeconds = seconds;
};

/** Read current lock timeout. Returns null if not set (API will use env default). */
const getLockTimeoutSeconds = (): number | null => _lockTimeoutSeconds;

export type TryLeadLockResponse =
  | {
      success: true;
      status: 'LOCKED' | 'ALREADY_OWNED';
      leadId: string;
      userName: string;
      ttl: number;
    }
  | { success: false; status: 'LOCKED_BY_OTHER'; leadId: string; lockedBy: string }
  | {
      success: false;
      status: 'MAX_REACHED';
      userName: string;
      maxLockedRecords: number;
      currentLockedCount: number;
    };

// ============================================
// Lead Lock API — single set of functions, if/else inside
// ============================================

/** GET lock status for a single lead. Returns { [leadId]: userName } or {}. */
export const getLeadLock = async (leadId: string): Promise<Record<string, string>> => {
  if (!leadId) return {};
  if (getLockStorageType() === 'SQL') {
    const response = await withLmaSafeApiLogging(
      {
        operation: 'getLeadLock',
        method: 'GET',
        endpoint: '/LMA/GetLeadLock',
      },
      () =>
        axios.get('/api/dap-api', {
          params: { url: '/LMA/GetLeadLock', leadId },
        })
    );
    return response.data || {};
  }
  const response = await withLmaSafeApiLogging(
    {
      operation: 'getLeadLock',
      method: 'GET',
      endpoint: '/api/lead-locks',
    },
    () =>
      axios.get<Record<string, string>>('/api/lead-locks', {
        params: { leadId },
      })
  );
  return response.data || {};
};

/** GET lock status for multiple leads. Returns { [leadId]: userName }. */
export const getLeadLocks = async (leadIds: string[]): Promise<Record<string, string>> => {
  if (!leadIds.length) return {};
  if (getLockStorageType() === 'SQL') {
    const response = await withLmaSafeApiLogging(
      {
        operation: 'getLeadLocks',
        method: 'GET',
        endpoint: '/LMA/GetLeadLocks',
      },
      () =>
        axios.get('/api/dap-api', {
          params: { url: '/LMA/GetLeadLocks', leadIds: leadIds.join(',') },
        })
    );
    return response.data || {};
  }
  const response = await withLmaSafeApiLogging(
    {
      operation: 'getLeadLocks',
      method: 'GET',
      endpoint: '/api/lead-locks',
    },
    () =>
      axios.get<Record<string, string>>('/api/lead-locks', {
        params: { leadIds: leadIds.join(',') },
      })
  );
  return response.data || {};
};

/** GET lead IDs locked by this user. */
export const getLockedLeadIdsByUser = async (userName: string): Promise<string[]> => {
  if (!userName) return [];
  if (getLockStorageType() === 'SQL') {
    const response = await withLmaSafeApiLogging(
      {
        operation: 'getLockedLeadIdsByUser',
        method: 'GET',
        endpoint: '/LMA/GetLockedLeadIdsByUser',
      },
      () =>
        axios.get('/api/dap-api', {
          params: { url: '/LMA/GetLockedLeadIdsByUser', userName },
        })
    );
    // SQL returns ints, Redis returns strings — normalize to strings
    return (response.data?.lockedLeadIds ?? []).map(String);
  }
  const response = await withLmaSafeApiLogging(
    {
      operation: 'getLockedLeadIdsByUser',
      method: 'GET',
      endpoint: '/api/lead-locks',
    },
    () =>
      axios.get<{ lockedLeadIds: string[] }>('/api/lead-locks', {
        params: { userName },
      })
  );
  return response.data?.lockedLeadIds ?? [];
};

/** POST try lock atomically. Returns structured result instead of throwing for lock conflicts. */
export const tryLeadLock = async (
  leadId: string,
  userName: string,
  maxLockedRecords: number
): Promise<TryLeadLockResponse> => {
  const ttl = getLockTimeoutSeconds();
  const max = Number.isFinite(maxLockedRecords) && maxLockedRecords > 0 ? maxLockedRecords : 1;

  if (getLockStorageType() === 'SQL') {
    const lockedLeadIds = await getLockedLeadIdsByUser(userName);
    if (lockedLeadIds.length >= max && !lockedLeadIds.includes(leadId)) {
      return {
        success: false,
        status: 'MAX_REACHED',
        userName,
        maxLockedRecords: max,
        currentLockedCount: lockedLeadIds.length,
      };
    }
    const locks = await getLeadLock(leadId);
    const lockedBy = locks[leadId];
    if (lockedBy && lockedBy !== userName) {
      return {
        success: false,
        status: 'LOCKED_BY_OTHER',
        leadId,
        lockedBy,
      };
    }
    await withLmaSafeApiLogging(
      {
        operation: 'setLeadLock',
        method: 'POST',
        endpoint: '/LMA/SetLeadLock',
      },
      () =>
        axios.post('/api/dap-api', {
          url: '/LMA/SetLeadLock',
          data: { leadId: parseInt(leadId, 10), userName },
        })
    );
    return {
      success: true,
      status: lockedBy === userName ? 'ALREADY_OWNED' : 'LOCKED',
      leadId,
      userName,
      ttl: ttl ?? 0,
    };
  }

  const response = await withLmaSafeApiLogging(
    {
      operation: 'setLeadLock',
      method: 'POST',
      endpoint: '/api/lead-locks',
    },
    () =>
      axios.post<TryLeadLockResponse>(
        '/api/lead-locks',
        {
          leadId,
          userName,
          maxLockedRecords: max,
          ...(ttl ? { ttl } : {}),
        },
        {
          params: { action: 'tryLock' },
        }
      )
  );
  return response.data;
};

/** DELETE single lock for leadId by userName (unlock). Optional secret for admin Redis clear. */
export const removeLeadLock = async (
  leadId: string,
  userName: string,
  secret?: string
): Promise<void> => {
  if (getLockStorageType() === 'SQL') {
    await withLmaSafeApiLogging(
      {
        operation: 'removeLeadLock',
        method: 'DELETE',
        endpoint: '/LMA/RemoveLeadLock',
      },
      () =>
        axios.delete('/api/dap-api', {
          params: { url: '/LMA/RemoveLeadLock', leadId, userName },
        })
    );
    return;
  }
  const params: Record<string, string> = { leadId, userName };
  if (secret?.trim()) params.secret = secret.trim();
  await withLmaSafeApiLogging(
    {
      operation: 'removeLeadLock',
      method: 'DELETE',
      endpoint: '/api/lead-locks',
    },
    () => axios.delete('/api/lead-locks', { params })
  );
};

/** DELETE all locks for userName. No secret = logout cleanup; with secret = admin clear. */
export const clearUserLocks = async (
  userName: string,
  secret?: string
): Promise<{ success: boolean; cleared?: boolean }> => {
  if (getLockStorageType() === 'SQL') {
    const response = await withLmaSafeApiLogging(
      {
        operation: 'clearUserLocks',
        method: 'DELETE',
        endpoint: '/LMA/ClearUserLocks',
      },
      () =>
        axios.delete('/api/dap-api', {
          params: { url: '/LMA/ClearUserLocks', userName },
        })
    );
    return { success: true, cleared: response.data?.cleared ?? response.data?.success ?? true };
  }
  const params: Record<string, string> = { userName };
  if (secret?.trim()) params.secret = secret.trim();
  const response = await withLmaSafeApiLogging(
    {
      operation: 'clearUserLocks',
      method: 'DELETE',
      endpoint: '/api/lead-locks',
    },
    () =>
      axios.delete<{ success: boolean; cleared?: boolean }>('/api/lead-locks', {
        params,
      })
  );
  return response.data;
};

// ============================================
// Admin-only — auto-routing (uses getLockStorageType())
// ============================================

/** Admin: clear a single lead's lock (whoever locked it). Auto-routes Redis/SQL. */
export const clearLockByLeadId = async (
  leadId: string,
  secret: string
): Promise<{ success: boolean; cleared?: boolean }> => {
  if (getLockStorageType() === 'SQL') {
    const response = await withLmaSafeApiLogging(
      {
        operation: 'clearLockByLeadId',
        method: 'DELETE',
        endpoint: '/LMA/ClearLockByLeadId',
      },
      () =>
        axios.delete('/api/dap-api', {
          params: { url: '/LMA/ClearLockByLeadId', leadId },
        })
    );
    return response.data;
  }
  const response = await withLmaSafeApiLogging(
    {
      operation: 'clearLockByLeadId',
      method: 'DELETE',
      endpoint: '/api/lead-locks',
    },
    () =>
      axios.delete<{ success: boolean; cleared?: boolean }>('/api/lead-locks', {
        params: { leadId, secret: secret?.trim() },
      })
  );
  return response.data;
};

/** Admin: clear all lead locks. Auto-routes Redis/SQL. */
export const clearAllLeadLocks = async (
  secret: string
): Promise<{ success: boolean; deleted?: number }> => {
  if (getLockStorageType() === 'SQL') {
    const response = await withLmaSafeApiLogging(
      {
        operation: 'clearAllLeadLocks',
        method: 'DELETE',
        endpoint: '/LMA/ClearAllLeadLocks',
      },
      () =>
        axios.delete('/api/dap-api', {
          params: { url: '/LMA/ClearAllLeadLocks' },
        })
    );
    return response.data;
  }
  const response = await withLmaSafeApiLogging(
    {
      operation: 'clearAllLeadLocks',
      method: 'DELETE',
      endpoint: '/api/lead-locks',
    },
    () =>
      axios.delete<{ success: boolean; deleted?: number }>('/api/lead-locks', {
        params: { clearAll: '1', secret },
      })
  );
  return response.data;
};

// ============================================
// Active Users (concurrent session limiting) — Redis only
// ============================================

/**
 * GET all usernames currently registered in the active LMA session store.
 * Returns a sorted array of unique usernames, or [] if the store is empty.
 */
export const getActiveUsers = async (): Promise<string[]> => {
  const response = await withLmaSafeApiLogging(
    {
      operation: 'getActiveUsers',
      method: 'GET',
      endpoint: '/api/lma-active-users',
    },
    () => axios.get<{ users: string[] }>('/api/lma-active-users')
  );
  return response.data?.users ?? [];
};

/** True if this username is still in the active-user Redis SET (e.g. after admin kick, false). */
export const isActiveUserMember = async (userName: string): Promise<boolean> => {
  if (!userName) return false;
  const response = await withLmaSafeApiLogging(
    {
      operation: 'isActiveUserMember',
      method: 'GET',
      endpoint: '/api/lma-active-users',
    },
    () =>
      axios.get<{ member: boolean }>('/api/lma-active-users', {
        params: { checkMember: userName },
      })
  );
  return !!response.data?.member;
};

/**
 * POST register the current user in the active session store.
 * Checked once per browser session (call site guards with sessionStorage).
 * Returns { success: true } if registered or already present.
 * Returns { success: false, limitReached: true } when max concurrent users is reached.
 * `maxUsers === 0` means unlimited (Sitecore max empty / null).
 */
export const registerActiveUser = async (
  userName: string,
  maxUsers: number,
  ttl: number
): Promise<{ success: boolean; limitReached?: boolean; kickBlocked?: boolean }> => {
  const response = await withLmaSafeApiLogging(
    {
      operation: 'registerActiveUser',
      method: 'POST',
      endpoint: '/api/lma-active-users',
    },
    () =>
      axios.post<{
        success: boolean;
        limitReached?: boolean;
        kickBlocked?: boolean;
      }>(
        '/api/lma-active-users',
        { userName, maxUsers, ttl },
        {
          validateStatus: (s) => s === 200 || s === 403 || s === 429,
        }
      )
  );
  if (response.status === 403) {
    return { success: false, kickBlocked: true };
  }
  if (response.status === 429) {
    return { success: false, limitReached: true };
  }
  return response.data;
};

/**
 * DELETE remove the current user from the active session store (logout cleanup).
 * No admin secret required — called from Logout and SessionEndedByAdmin pages.
 */
export const unregisterActiveUser = async (userName: string): Promise<void> => {
  await withLmaSafeApiLogging(
    {
      operation: 'unregisterActiveUser',
      method: 'DELETE',
      endpoint: '/api/lma-active-users',
    },
    () => axios.delete('/api/lma-active-users', { params: { activeUser: userName } })
  );
};

/**
 * Admin: DELETE kick a single user from the active session store.
 * Requires LMA_CACHE_CLEAR_SECRET.
 * `removed` is true only if that user had an active session slot in Redis (deleted).
 */
export const kickActiveUser = async (
  userName: string,
  secret: string
): Promise<{ success: boolean; removed: boolean }> => {
  const response = await withLmaSafeApiLogging(
    {
      operation: 'kickActiveUser',
      method: 'DELETE',
      endpoint: '/api/lma-active-users',
    },
    () =>
      axios.delete<{ success: boolean; removed: boolean }>('/api/lma-active-users', {
        params: { activeUser: userName, secret },
      })
  );
  return response.data;
};

/**
 * Admin: DELETE clear the entire active session store (kick all users).
 * Requires LMA_CACHE_CLEAR_SECRET.
 * `deleted` is the number of session keys removed (0 if none).
 */
export const kickAllActiveUsers = async (
  secret: string
): Promise<{ success: boolean; deleted: number }> => {
  const response = await withLmaSafeApiLogging(
    {
      operation: 'kickAllActiveUsers',
      method: 'DELETE',
      endpoint: '/api/lma-active-users',
    },
    () =>
      axios.delete<{ success: boolean; deleted: number }>('/api/lma-active-users', {
        params: { kickAll: '1', secret },
      })
  );
  return response.data;
};

/**
 * Sends a patient record to Clinical Conductor (CC)
 *
 * @param payload - The patient data to send
 * @param options - Lock enforcement options from the LeadManagement/UI layer
 * @returns Promise with the API response
 * @throws Error if the API call fails
 */

export interface SendToCCOptions {
  isLockingEnabled?: boolean;
  userName?: string;
}

export const sendToCC = async (payload: SendToCCPayload, options: SendToCCOptions = {}) => {
  const isLockingEnabled = options.isLockingEnabled === true;
  const userName = options.userName?.trim() || '';
  if (isLockingEnabled) {
    if (!userName) {
      throw new Error('Missing username for Send to CC');
    }
    const leadIdStr = String(payload.leadId);
    const locks = await getLeadLock(leadIdStr);
    const lockedBy = locks[leadIdStr];
    if (lockedBy == null || lockedBy === '') {
      throw new Error(
        'The locking period for this record appears to have timed out. Please try re-locking it, or reach out to the admin if the issue persists.'
      );
    }
    if (lockedBy !== userName) {
      throw new Error(
        'This lead is locked by another user. Send to CC requires an active lock by you on this record.'
      );
    }
  }

  const response = await withLmaSafeApiLogging(
    {
      operation: 'sendToCC',
      method: 'POST',
      endpoint: '/CCT/SendToCC',
    },
    () =>
      axios.post('/api/dap-api', {
        url: '/CCT/SendToCC',
        data: payload,
      })
  );
  return response.data;
};

export interface SyncedResource {
  conditionId?: number;
  procedureId?: number;
  id?: number;
  immunizationId?: number;
  allergyId?: number;
  familyMemberId?: number;
  deviceId?: number;
  value: string;
  codingSystem: string;
  synced: boolean;
  /** Set for batch GetSyncedResources; comma-separated when merged across leads */
  leadId?: string;
}

export interface GetSyncedResourcesResponse {
  conditions?: SyncedResource[];
  procedures?: SyncedResource[];
  medications?: SyncedResource[];
  immunizations?: SyncedResource[];
  allergies?: SyncedResource[];
  familyMemberHistory?: SyncedResource[];
  devices?: SyncedResource[];
}

function mergeLeadIdLabels(a?: string, b?: string): string | undefined {
  const parts = [...(a ? a.split(',') : []), ...(b ? b.split(',') : [])]
    .map((x) => x.trim())
    .filter(Boolean);
  if (parts.length === 0) return undefined;
  const uniq = [...new Set(parts)].sort((x, y) => Number(x) - Number(y) || x.localeCompare(y));
  return uniq.join(', ');
}

function mergeOptionalSyncedLists<T extends { synced?: boolean; leadId?: string }>(
  lists: (T[] | undefined)[]
): T[] | undefined {
  const map = new Map<string, T>();
  const keyOf = (item: T) => {
    const id =
      (item as any).conditionId ??
      (item as any).procedureId ??
      (item as any).id ??
      (item as any).immunizationId ??
      (item as any).allergyId ??
      (item as any).deviceId ??
      (item as any).familyMemberId;
    return String(id ?? JSON.stringify(item));
  };
  for (const list of lists) {
    if (!list) continue;
    for (const item of list) {
      const k = keyOf(item);
      const prev = map.get(k);
      if (!prev) map.set(k, { ...item });
      else
        map.set(k, {
          ...prev,
          ...item,
          synced: Boolean(prev.synced) || Boolean(item.synced),
          leadId: mergeLeadIdLabels(prev.leadId, item.leadId),
        });
    }
  }
  const out = [...map.values()];
  return out.length ? out : undefined;
}

function annotateResourcesWithLeadId(
  resources: GetSyncedResourcesResponse,
  leadId: number
): GetSyncedResourcesResponse {
  const tag = <T extends SyncedResource>(list: T[] | undefined): T[] | undefined =>
    list?.map((item) => ({ ...item, leadId: String(leadId) }));

  return {
    conditions: tag(resources.conditions),
    procedures: tag(resources.procedures),
    medications: tag(resources.medications),
    immunizations: tag(resources.immunizations),
    allergies: tag(resources.allergies),
    devices: tag(resources.devices),
    familyMemberHistory: tag(resources.familyMemberHistory),
  };
}

export interface SyncedResourcesResultItem {
  guestRef: string;
  enrollmentId: string;
  leadId: number;
  registrationDateTime?: string | null;
  resources: GetSyncedResourcesResponse;
}

export interface GetSyncedResourcesBatchApiResponse {
  items: SyncedResourcesResultItem[];
}

function mergeGetSyncedResourcesBatch(
  batch: GetSyncedResourcesBatchApiResponse
): GetSyncedResourcesResponse {
  const items = batch.items ?? [];
  if (items.length === 0) {
    return {
      conditions: [],
      procedures: [],
      medications: [],
      immunizations: [],
      allergies: [],
      devices: [],
      familyMemberHistory: [],
    };
  }

  const annotated = items.map((i) => annotateResourcesWithLeadId(i.resources, i.leadId));

  if (annotated.length === 1) return annotated[0];

  return {
    conditions: mergeOptionalSyncedLists(annotated.map((r) => r.conditions)),
    procedures: mergeOptionalSyncedLists(annotated.map((r) => r.procedures)),
    medications: mergeOptionalSyncedLists(annotated.map((r) => r.medications)),
    immunizations: mergeOptionalSyncedLists(annotated.map((r) => r.immunizations)),
    allergies: mergeOptionalSyncedLists(annotated.map((r) => r.allergies)),
    devices: mergeOptionalSyncedLists(annotated.map((r) => r.devices)),
    familyMemberHistory: mergeOptionalSyncedLists(annotated.map((r) => r.familyMemberHistory)),
  };
}

export interface GetSyncedResourcesParams {
  guestRefs: string[];
  enrollmentIds: string[];
  leadIds: number[];
  leadStatus: 'Requested' | 'Processed';
}

/**
 * Flat query shape for dap-api GET: avoids array values in `req.query` (dap-api forwards with a simple
 * objectToQueryString). Indexed keys bind to list query parameters on ASP.NET Core (GuestRefs, EnrollmentIds, LeadIds).
 */
export function getSyncedResourcesQueryFlat(
  params: GetSyncedResourcesParams
): Record<string, string | number> {
  const out: Record<string, string | number> = { leadStatus: params.leadStatus };
  params.guestRefs.forEach((g, i) => {
    out[`guestRefs[${i}]`] = g;
  });
  params.enrollmentIds.forEach((e, i) => {
    out[`enrollmentIds[${i}]`] = e;
  });
  params.leadIds.forEach((id, i) => {
    out[`leadIds[${i}]`] = id;
  });
  return out;
}

/**
 * Get synced resources from CDP (batch-capable; merges multiple lead triples into one view).
 *
 * @param params - Parallel guestRefs, enrollmentIds, leadIds plus leadStatus (matches ~/CCT/GetSyncedResources).
 */
export const getSyncedResources = async (
  params: GetSyncedResourcesParams
): Promise<GetSyncedResourcesResponse> => {
  const queryString = objectToQueryString(getSyncedResourcesQueryFlat(params));
  const url = `/CCT/GetSyncedResources&${queryString}`;

  const response = await withLmaSafeApiLogging(
    {
      operation: 'getSyncedResources',
      method: 'GET',
      endpoint: '/CCT/GetSyncedResources',
    },
    () => axios.get('/api/dap-api?url=' + url)
  );

  const body = response.data;
  if (body?.items && Array.isArray(body.items)) {
    return mergeGetSyncedResourcesBatch(body as GetSyncedResourcesBatchApiResponse);
  }
  const flat = body as GetSyncedResourcesResponse;
  if (params.leadIds?.length === 1) {
    return annotateResourcesWithLeadId(flat, params.leadIds[0]);
  }
  return flat;
};

export interface SyncResourcesPayload {
  ccId: string;
  userId: string;
  guestRef: string;
  enrollmentId: string;
  allResourcesSelected: boolean;
  resources: {
    conditions?: Array<{
      conditionId: number;
      value?: string | null;
      codingSystem?: string | null;
      startDate?: string | null;
      endDate?: string | null;
      ageAtOnset?: number | null;
      comment?: string | null;
      leadIds: number[];
    }> | null;
    procedures?: Array<{
      procedureId: number;
      value?: string | null;
      codingSystem?: string | null;
      comment?: string | null;
      cptCode?: string | null;
      procedureBy?: string | null;
      date?: string | null;
      leadIds: number[];
    }> | null;
    medications?: Array<{
      id: number;
      value?: string | null;
      codingSystem?: string | null;
      dosage?: string | null;
      startDate?: string | null;
      endDate?: string | null;
      comment?: string | null;
      routeId?: number | null;
      leadIds: number[];
    }> | null;
    immunizations?: Array<{
      immunizationId: number;
      value?: string | null;
      codingSystem?: string | null;
      comment?: string | null;
      location?: string | null;
      date?: string | null;
      leadIds: number[];
    }> | null;
    allergies?: Array<{
      allergyId: number;
      value?: string | null;
      codingSystem?: string | null;
      reaction?: string | null;
      comment?: string | null;
      startDate?: string | null;
      endDate?: string | null;
      leadIds: number[];
    }> | null;
    familyMemberHistory?: Array<{
      conditionId: number;
      familyMemberId: number;
      value?: string | null;
      codingSystem?: string | null;
      ageAtOnset?: number | null;
      startDate?: string | null;
      endDate?: string | null;
      comment?: string | null;
      leadIds: number[];
    }> | null;
    devices?: Array<{
      deviceId: number;
      value?: string | null;
      codingSystem?: string | null;
      comment?: string | null;
      leadIds: number[];
    }> | null;
  };
}

/**
 * Sync selected resources to CDP
 * Sends unsynced resources from the Processed tab to Clinical Conductor
 *
 * @param payload - The sync payload with selected resources
 * @returns Promise with the API response
 */
export const syncResources = async (payload: SyncResourcesPayload): Promise<any> => {
  const response = await withLmaSafeApiLogging(
    {
      operation: 'syncResources',
      method: 'POST',
      endpoint: '/LMA/SyncResources',
    },
    () =>
      axios.post('/api/dap-api', {
        url: '/LMA/SyncResources',
        data: payload,
      })
  );

  return response.data;
};
